"use client"

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import apiService from '../services/apiService';
import { DashboardHeader } from '../components/ui/dashboard-header';
import { Card, CardContent } from '../components/ui/card';
import { AlertTriangle, Cpu } from 'lucide-react';
import { CustomOverlaySidebar } from '../components/ui/custom-overlay-sidebar';
import { Pagination } from '../components/ui/pagination';
import { GatewayMetricsSheet } from '../components/ui/gateway-metrics-sheet';
import { PlatformBreakdown } from '../components/ui/platform-breakdown';
import { GatewayRow } from '../components/ui/gateway-row';
import { isGatewayDeviceType } from '../utils/deviceType';
import {
  getGatewayStatus,
  getGatewayPlatform,
  buildGatewayCsv,
  buildPlatformItems,
  PLATFORM_DISPLAY_NAMES,
} from '../utils/gatewayHelpers';
import { downloadCsv } from '../utils/csvExport';
import { useLogout } from '../hooks/useLogout';
import { useSidebar } from '../hooks/useSidebar';
import type { DeviceStats } from '../types';

const GatewaysPage: React.FC = () => {
  const [stats, setStats] = useState<DeviceStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { sidebarOpen, closeSidebar, toggleSidebar } = useSidebar();
  const handleLogout = useLogout();
  const [selectedGateway, setSelectedGateway] = useState<DeviceStats | null>(null);
  const gatewayListRef = useRef<HTMLDivElement>(null);

  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'offline'>('all');
  const [platformFilter, setPlatformFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [sortBy, setSortBy] = useState<'lastSeen' | 'platform' | 'status'>('lastSeen');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const fetchStats = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const response = await apiService.getDeviceStats({ limit: 1000 });
      if (response.success) {
        setStats(response.data);
        setError(null);
      } else {
        setError(response.error || 'Failed to fetch stats');
      }
    } catch {
      setError('An unexpected error occurred while fetching data.');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    const id = setInterval(() => fetchStats(true), 60_000);
    return () => clearInterval(id);
  }, []);

  const gatewayRecords = useMemo(() => {
    const byGateway = new Map<string, DeviceStats>();
    stats.forEach((stat) => {
      if (!isGatewayDeviceType(stat.device_type)) return;
      const key = stat.uuid || `${stat.client_id}-${stat.site_id}`;
      const existing = byGateway.get(key);
      if (!existing || new Date(stat.event_received_time) > new Date(existing.event_received_time)) {
        byGateway.set(key, stat);
      }
    });
    return Array.from(byGateway.values());
  }, [stats]);

  const statusCounts = useMemo(
    () => gatewayRecords.reduce(
      (acc, stat) => { acc[getGatewayStatus(stat)] += 1; return acc; },
      { active: 0, offline: 0, unknown: 0 }
    ),
    [gatewayRecords]
  );

  const activeGateways = useMemo(
    () => gatewayRecords.filter((s) => getGatewayStatus(s) === 'active'),
    [gatewayRecords]
  );

  const inactiveGateways = useMemo(
    () => gatewayRecords.filter((s) => getGatewayStatus(s) !== 'active'),
    [gatewayRecords]
  );

  const activePlatformItems = useMemo(() => {
    const counts: Record<string, number> = {};
    activeGateways.forEach((s) => {
      const p = getGatewayPlatform(s);
      counts[p] = (counts[p] || 0) + 1;
    });
    return buildPlatformItems(counts, 'active');
  }, [activeGateways]);

  const inactivePlatformItems = useMemo(() => {
    const counts: Record<string, number> = {};
    inactiveGateways.forEach((s) => {
      const p = getGatewayPlatform(s);
      counts[p] = (counts[p] || 0) + 1;
    });
    return buildPlatformItems(counts, 'inactive');
  }, [inactiveGateways]);

  const scrollToList = useCallback(() => {
    setTimeout(() => gatewayListRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  }, []);

  const handleStatusCardClick = (status: 'active' | 'offline') => {
    setStatusFilter(status);
    setPlatformFilter('all');
    scrollToList();
  };

  const handlePlatformFilterClick = useCallback((platform: string, filterStatus: 'active' | 'offline') => {
    setStatusFilter(filterStatus);
    setPlatformFilter(platform);
    scrollToList();
  }, [scrollToList]);

  const uniquePlatforms = useMemo(() => {
    const platforms = new Set<string>();
    gatewayRecords.forEach((s) => platforms.add(getGatewayPlatform(s)));
    return Array.from(platforms).sort();
  }, [gatewayRecords]);

  const filteredAndSortedGateways = useMemo(() => {
    let filtered = [...gatewayRecords];

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter((s) =>
        s.client_id?.toLowerCase().includes(query) ||
        s.site_id?.toLowerCase().includes(query) ||
        s.uuid?.toLowerCase().includes(query)
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter((s) => {
        const category = getGatewayStatus(s);
        return statusFilter === 'active' ? category === 'active' : category !== 'active';
      });
    }

    if (platformFilter !== 'all') {
      filtered = filtered.filter((s) => getGatewayPlatform(s) === platformFilter);
    }

    filtered.sort((a, b) => {
      if (sortBy === 'lastSeen') {
        return new Date(b.event_received_time).getTime() - new Date(a.event_received_time).getTime();
      }
      if (sortBy === 'platform') {
        return getGatewayPlatform(a).localeCompare(getGatewayPlatform(b));
      }
      if (sortBy === 'status') {
        const sa = getGatewayStatus(a);
        const sb = getGatewayStatus(b);
        if (sa === sb) return new Date(b.event_received_time).getTime() - new Date(a.event_received_time).getTime();
        return sa === 'active' ? -1 : 1;
      }
      return 0;
    });

    return filtered;
  }, [gatewayRecords, statusFilter, platformFilter, searchQuery, sortBy]);

  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, platformFilter, searchQuery, sortBy, pageSize]);

  const totalPages = Math.max(1, Math.ceil(filteredAndSortedGateways.length / pageSize));

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const pagedGateways = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredAndSortedGateways.slice(start, start + pageSize);
  }, [filteredAndSortedGateways, currentPage, pageSize]);

  const handleExportGatewaysCsv = () => {
    const dateStamp = new Date().toISOString().slice(0, 10);
    const parts: string[] = [];
    if (searchQuery.trim()) parts.push(`search-${searchQuery.trim().replace(/\s+/g, '_')}`);
    if (statusFilter !== 'all') parts.push(statusFilter === 'active' ? 'online' : 'offline');
    if (platformFilter !== 'all') parts.push(platformFilter);
    if (sortBy) parts.push(`sort-${sortBy}`);
    const suffix = parts.length > 0 ? `_${parts.join('_')}` : '';
    downloadCsv(buildGatewayCsv(filteredAndSortedGateways), `gateways${suffix}_${dateStamp}.csv`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen" style={{ backgroundColor: '#0f0f0f', color: '#e2e8f0' }}>
        <h2>Loading gateways...</h2>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: '#0f0f0f', minHeight: '100vh' }}>
      <CustomOverlaySidebar isOpen={sidebarOpen} onClose={closeSidebar} />

      <div className="w-full min-h-screen">
        <DashboardHeader
          onLogout={handleLogout}
          onSidebarToggle={toggleSidebar}
          title="Gateways"
          showFilters={false}
        />

        <div className="p-6 space-y-6">
          {/* Summary cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card
              onClick={() => handleStatusCardClick('active')}
              className="border-0 relative overflow-hidden transition-all duration-300 ease-in-out hover:scale-105 hover:shadow-2xl cursor-pointer"
              style={{
                background: 'linear-gradient(135deg, #005c5a 0%, #007c7a 100%)',
                boxShadow: statusFilter === 'active' && platformFilter === 'all'
                  ? '0 0 0 3px #22c55e, 0 8px 32px rgba(0, 0, 0, 0.3)'
                  : '0 8px 32px rgba(0, 0, 0, 0.3)',
              }}
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <Cpu className="h-8 w-8 transition-transform duration-300 hover:scale-110" style={{ color: '#ffffff' }} />
                  <div
                    className="text-xs px-3 py-1 rounded-full font-medium"
                    style={{ backgroundColor: 'rgba(255, 255, 255, 0.15)', color: '#ffffff' }}
                  >
                    {statusFilter === 'active' && platformFilter === 'all' ? 'Filtered ✓' : 'Online'}
                  </div>
                </div>
                <div className="space-y-1">
                  <div className="text-2xl font-bold" style={{ color: '#ffffff' }}>{statusCounts.active}</div>
                  <div className="text-sm font-medium" style={{ color: 'rgba(255, 255, 255, 0.8)' }}>Online Gateways</div>
                </div>
              </CardContent>
            </Card>

            <Card
              onClick={() => handleStatusCardClick('offline')}
              className="border-0 relative overflow-hidden transition-all duration-300 ease-in-out hover:scale-105 hover:shadow-2xl cursor-pointer"
              style={{
                background: 'linear-gradient(135deg, #3a0f15 0%, #6b1d2a 100%)',
                boxShadow: statusFilter === 'offline' && platformFilter === 'all'
                  ? '0 0 0 3px #f87171, 0 8px 32px rgba(0, 0, 0, 0.3)'
                  : '0 8px 32px rgba(0, 0, 0, 0.3)',
              }}
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <AlertTriangle className="h-8 w-8 transition-transform duration-300 hover:scale-110" style={{ color: '#ffffff' }} />
                  <div
                    className="text-xs px-3 py-1 rounded-full font-medium"
                    style={{ backgroundColor: 'rgba(255, 255, 255, 0.15)', color: '#ffffff' }}
                  >
                    {statusFilter === 'offline' && platformFilter === 'all' ? 'Filtered ✓' : 'Offline'}
                  </div>
                </div>
                <div className="space-y-1">
                  <div className="text-2xl font-bold" style={{ color: '#ffffff' }}>{statusCounts.offline + statusCounts.unknown}</div>
                  <div className="text-sm font-medium" style={{ color: 'rgba(255, 255, 255, 0.8)' }}>Offline Gateways</div>
                  {statusCounts.unknown > 0 && (
                    <div className="text-xs" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>Includes {statusCounts.unknown} unknown</div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Platform breakdown */}
          <div className="rounded-2xl px-8 py-6 space-y-6" style={{ backgroundColor: '#0f1717', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
            <PlatformBreakdown
              title="Online platform breakdown"
              totalCount={statusCounts.active}
              items={activePlatformItems}
              variant="online"
              statusFilter={statusFilter}
              platformFilter={platformFilter}
              onPlatformClick={(platform) => handlePlatformFilterClick(platform, 'active')}
            />
            <PlatformBreakdown
              title="Offline platform breakdown"
              totalCount={statusCounts.offline + statusCounts.unknown}
              items={inactivePlatformItems}
              variant="offline"
              statusFilter={statusFilter}
              platformFilter={platformFilter}
              onPlatformClick={(platform) => handlePlatformFilterClick(platform, 'offline')}
            />
          </div>

          {/* Filters and controls */}
          <div className="rounded-2xl px-6 py-4" style={{ backgroundColor: '#0f1717', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex items-center gap-2">
                  <label className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>Search:</label>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Client ID, Site ID, or UUID..."
                    className="px-3 py-2 rounded-lg text-sm w-64"
                    style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', color: '#ffffff', border: '1px solid rgba(255, 255, 255, 0.1)', outline: 'none' }}
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="text-xs px-2 py-1 rounded"
                      style={{ color: 'rgba(255, 255, 255, 0.6)', backgroundColor: 'rgba(255, 255, 255, 0.05)' }}
                    >
                      Clear
                    </button>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <label className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>Status:</label>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value as 'all' | 'active' | 'offline')}
                    className="px-3 py-2 rounded-lg text-sm"
                    style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', color: '#ffffff', border: '1px solid rgba(255, 255, 255, 0.1)' }}
                  >
                    <option value="all">All</option>
                    <option value="active">Online</option>
                    <option value="offline">Offline</option>
                  </select>
                </div>

                <div className="flex items-center gap-2">
                  <label className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>Platform:</label>
                  <select
                    value={platformFilter}
                    onChange={(e) => setPlatformFilter(e.target.value)}
                    className="px-3 py-2 rounded-lg text-sm"
                    style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', color: '#ffffff', border: '1px solid rgba(255, 255, 255, 0.1)' }}
                  >
                    <option value="all">All</option>
                    {uniquePlatforms.map((p) => (
                      <option key={p} value={p}>{PLATFORM_DISPLAY_NAMES[p] || p}</option>
                    ))}
                  </select>
                </div>

                <div className="flex items-center gap-2">
                  <label className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>Sort by:</label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as 'lastSeen' | 'platform' | 'status')}
                    className="px-3 py-2 rounded-lg text-sm"
                    style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', color: '#ffffff', border: '1px solid rgba(255, 255, 255, 0.1)' }}
                  >
                    <option value="lastSeen">Last Seen</option>
                    <option value="platform">Platform</option>
                    <option value="status">Status</option>
                  </select>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <label className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>Per page:</label>
                  <select
                    value={pageSize}
                    onChange={(e) => setPageSize(Number(e.target.value))}
                    className="px-3 py-2 rounded-lg text-sm"
                    style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', color: '#ffffff', border: '1px solid rgba(255, 255, 255, 0.1)' }}
                  >
                    {[10, 20, 50, 100].map((size) => (
                      <option key={size} value={size}>{size}</option>
                    ))}
                  </select>
                </div>

                <button
                  onClick={() => fetchStats()}
                  disabled={loading}
                  className="px-4 py-2 rounded-lg text-sm font-medium transition-all"
                  style={{ backgroundColor: 'rgba(0, 169, 167, 0.2)', color: '#00A9A7', border: '1px solid rgba(0, 169, 167, 0.3)' }}
                >
                  {loading ? 'Refreshing...' : 'Refresh'}
                </button>

                <button
                  onClick={handleExportGatewaysCsv}
                  className="px-4 py-2 rounded-lg text-sm font-medium transition-all"
                  style={{ backgroundColor: 'rgba(59, 130, 246, 0.18)', color: '#93c5fd', border: '1px solid rgba(59, 130, 246, 0.3)' }}
                >
                  Export CSV
                </button>
              </div>
            </div>

            <div className="mt-3 text-sm" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>
              Showing {filteredAndSortedGateways.length === 0 ? 0 : (currentPage - 1) * pageSize + 1}
              -{Math.min(currentPage * pageSize, filteredAndSortedGateways.length)} of {filteredAndSortedGateways.length} gateways
            </div>
          </div>

          {/* Gateway list */}
          <div ref={gatewayListRef} className="rounded-2xl px-6 py-6 space-y-3" style={{ backgroundColor: '#0f1717', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
            <div className="flex items-center justify-between mb-4">
              <div className="text-lg font-semibold" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>Gateway Details</div>
              <div className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                Online {statusCounts.active} • Offline {statusCounts.offline + statusCounts.unknown} • Total {gatewayRecords.length}
              </div>
            </div>

            {(statusFilter !== 'all' || platformFilter !== 'all' || searchQuery.trim()) && (
              <div
                className="flex items-center justify-between rounded-lg px-4 py-2 mb-2"
                style={{ backgroundColor: 'rgba(148, 163, 184, 0.08)', border: '1px solid rgba(148, 163, 184, 0.2)' }}
              >
                <span className="text-sm" style={{ color: '#94a3b8' }}>
                  Filtering:{' '}
                  {searchQuery.trim() && <strong>Search: "{searchQuery}"</strong>}
                  {searchQuery.trim() && (platformFilter !== 'all' || statusFilter !== 'all') && ' · '}
                  {platformFilter !== 'all' && <strong>{PLATFORM_DISPLAY_NAMES[platformFilter] || platformFilter}</strong>}
                  {platformFilter !== 'all' && statusFilter !== 'all' && ' · '}
                  {statusFilter !== 'all' && <strong>{statusFilter === 'active' ? 'Online' : 'Offline'}</strong>}
                  {' '}— {filteredAndSortedGateways.length} result{filteredAndSortedGateways.length !== 1 ? 's' : ''}
                </span>
              </div>
            )}

            {pagedGateways.length === 0 ? (
              <div
                className="text-center py-10 rounded-xl"
                style={{ backgroundColor: 'rgba(255, 255, 255, 0.03)', border: '1px dashed rgba(255, 255, 255, 0.2)' }}
              >
                <div className="text-base font-semibold" style={{ color: 'rgba(255, 255, 255, 0.85)' }}>No gateway details yet</div>
                <div className="text-sm mt-2" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                  Once edge devices start sending data, each gateway will appear here with status, platform, and last-seen time.
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                {pagedGateways.map((stat) => (
                  <GatewayRow key={stat.uuid} stat={stat} onSelect={setSelectedGateway} />
                ))}
              </div>
            )}

            <div className="pt-4">
              <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
            </div>
          </div>

          {error && <div className="text-sm" style={{ color: '#f87171' }}>{error}</div>}
        </div>
      </div>

      <GatewayMetricsSheet
        gateway={selectedGateway}
        open={selectedGateway !== null}
        onClose={() => setSelectedGateway(null)}
      />
    </div>
  );
};

export default GatewaysPage;
